import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import {
  CTAButton,
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  NamedLink,
  Page,
  SearchListingCardV2,
  SearchShellV2,
} from '../../components';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import { cityContent } from '../../data/cityLandingContent';
import { FEATURED_LISTINGS_COUNT, getFeaturedListings } from '../LandingPage/LandingPage.duck';
import { fetchCityListingCounts } from '../LandingPageCities/LandingPageCities.duck';
import { defaultMarketplaceSearchParams } from '../../util/defaultSearchParams';
import { stringify } from '../../util/urlHelpers';
import facebookImage from '../../assets/uupo-share-facebook-1200x630.png';
import heroImage from '../../assets/landingRefresh/hero-storefront-street.jpg';
import storefrontPreview from '../../assets/landingRefresh/storefront-mannequins.jpg';
import discoverIcon from '../../components/SectionTwoUuperNew/images/Discover.png';
import bookIcon from '../../components/SectionTwoUuperNew/images/Book.png';
import comeIcon from '../../components/SectionTwoUuperNew/images/Come.png';
import listIcon from '../../components/SectionTwoUuperNew/images/List.png';
import acceptIcon from '../../components/SectionTwoUuperNew/images/Accept.png';
import earnIcon from '../../components/SectionTwoUuperNew/images/Earn.png';

import css from './LandingUuper.module.css';

const ShieldIcon = () => (
  <svg viewBox="0 0 48 48" className={css.featureIcon} aria-hidden="true">
    <path d="M24 7l12 5.1v8.4c0 8.7-4.9 15.9-12 19.5-7.1-3.6-12-10.8-12-19.5v-8.4z" />
    <path d="M18 23.5l4.2 4.2L30 20" />
  </svg>
);

const CardIcon = () => (
  <svg viewBox="0 0 48 48" className={css.featureIcon} aria-hidden="true">
    <rect x="8" y="12" width="32" height="24" rx="6" />
    <path d="M8 20h32" />
    <path d="M16 29h8" />
  </svg>
);

const SupportIcon = () => (
  <svg viewBox="0 0 48 48" className={css.featureIcon} aria-hidden="true">
    <path d="M15 24a9 9 0 0118 0" />
    <path d="M12 25v5a4 4 0 004 4h3v-9h-3a4 4 0 00-4 4z" />
    <path d="M36 25v5a4 4 0 01-4 4h-3v-9h3a4 4 0 014 4z" />
    <path d="M24 34v3a4 4 0 01-4 4" />
  </svg>
);

const formatCityName = cityNameKey =>
  cityNameKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

const resolveCityLabel = (intl, key) => {
  const translated = intl.formatMessage({ id: `LandingPageCities.${key}` });
  return translated === `LandingPageCities.${key}` ? formatCityName(key) : translated;
};

// Height of the sticky desktop header: the hero search counts as off screen once
// it slides under it, and the header then shows its own search.
const DESKTOP_HEADER_HEIGHT = 80;

const STORY_AUDIENCE_UPER = 'uuper';
const STORY_AUDIENCE_UUPO = 'uupo';
const FEATURED_CITY_CARD_FALLBACK_KEYS = ['madrid', 'barcelona', 'palmaDeMallorca', 'valencia'];

export const LandingUuperComponent = props => {
  const {
    history,
    intl,
    scrollingDisabled,
    featuredListings,
    latestListings,
    isAuthenticated,
    featuredListingsLoaded,
    featuredListingsInProgress,
    cityListingCounts,
    cityListingCountsLoaded,
    fetchCityListingCountsInProgress,
    onLoadFeaturedListings,
    onFetchCityListingCounts,
  } = props;
  const [storyAudience, setStoryAudience] = useState(STORY_AUDIENCE_UPER);
  const heroSearchRef = useRef(null);
  const [isHeroSearchInView, setIsHeroSearchInView] = useState(true);

  useEffect(() => {
    const node = heroSearchRef.current;
    if (!node || typeof window === 'undefined' || !window.IntersectionObserver) {
      return undefined;
    }

    const observer = new window.IntersectionObserver(
      ([entry]) => setIsHeroSearchInView(entry.isIntersecting),
      { rootMargin: `-${DESKTOP_HEADER_HEIGHT}px 0px 0px 0px` }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const handleApplySearchParams = searchParams => {
    history.push(createResourceLocatorString('SearchPage', routeConfiguration(), {}, searchParams));
  };

  useEffect(() => {
    if (!featuredListingsLoaded && !featuredListingsInProgress) {
      onLoadFeaturedListings();
    }
  }, [featuredListingsLoaded, featuredListingsInProgress, onLoadFeaturedListings]);

  useEffect(() => {
    if (!cityListingCountsLoaded && !fetchCityListingCountsInProgress) {
      onFetchCityListingCounts();
    }
  }, [cityListingCountsLoaded, fetchCityListingCountsInProgress, onFetchCityListingCounts]);

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'LandingUuper.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({ id: 'LandingUuper.schemaDescription' });
  const schemaImage = `${config.canonicalRootURL}${facebookImage}`;
  const featuredCityCardKeys = useMemo(() => {
    if (!cityListingCountsLoaded) {
      return FEATURED_CITY_CARD_FALLBACK_KEYS;
    }

    return Object.keys(cityContent)
      .map((key, index) => ({ key, index }))
      .sort((a, b) => {
        const countA = Number.isFinite(cityListingCounts[a.key])
          ? cityListingCounts[a.key]
          : -1;
        const countB = Number.isFinite(cityListingCounts[b.key])
          ? cityListingCounts[b.key]
          : -1;

        return countB - countA || a.index - b.index;
      })
      .slice(0, 4)
      .map(({ key }) => key);
  }, [cityListingCounts, cityListingCountsLoaded]);
  const signupRouteName = isAuthenticated ? 'ProfileSettingsPage' : 'SignupPage';
  const defaultSearchLink = { search: `?${stringify(defaultMarketplaceSearchParams)}` };
  const isUuperAudience = storyAudience === STORY_AUDIENCE_UPER;
  const displayedListings = useMemo(() => {
    const listingMap = new Map();
    featuredListings.forEach(listing => {
      if (listing?.id?.uuid) {
        listingMap.set(listing.id.uuid, listing);
      }
    });
    latestListings.forEach(listing => {
      if (listing?.id?.uuid && !listingMap.has(listing.id.uuid)) {
        listingMap.set(listing.id.uuid, listing);
      }
    });
    return Array.from(listingMap.values()).slice(0, FEATURED_LISTINGS_COUNT);
  }, [featuredListings, latestListings]);
  const storyCards = isUuperAudience
    ? [
        {
          icon: discoverIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Client.card1.title" />,
          description: <FormattedMessage id="LandingUuper.step1Description" />,
        },
        {
          icon: bookIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Client.card2.title" />,
          description: <FormattedMessage id="LandingUuper.step2Description" />,
        },
        {
          icon: comeIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Client.card3.title" />,
          description: <FormattedMessage id="LandingUuper.step3Description" />,
        },
      ]
    : [
        {
          icon: listIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Seller.card1.title" />,
          description: <FormattedMessage id="LandingUuper.sellerStep1Description" />,
        },
        {
          icon: acceptIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Seller.card2.title" />,
          description: <FormattedMessage id="LandingUuper.sellerStep2Description" />,
        },
        {
          icon: earnIcon,
          title: <FormattedMessage id="SectionTwoUuperNew.Seller.card3.title" />,
          description: <FormattedMessage id="LandingUuper.sellerStep3Description" />,
        },
      ];

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      primaryKeywords={intl.formatMessage({ id: 'LandingUuper.keywords' })}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <LandingHeader currentPage="LandingPage" hideSearch={isHeroSearchInView} />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain className={css.mainWrapper}>
          <section
            className={css.heroSection}
            style={{
              backgroundImage: `url(${heroImage})`,
            }}
          >
            <div className={css.heroInner}>
              <h1 className={css.heroTitle}>
                <FormattedMessage id="SectionHeroUuper.title" />
              </h1>
              <p className={css.heroDescription}>
                <FormattedMessage id="LandingUuper.heroSubtitle" />
              </p>
              <div className={css.heroSearch} ref={heroSearchRef}>
                <SearchShellV2
                  intl={intl}
                  onApplySearchParams={handleApplySearchParams}
                  variant="hero"
                />
              </div>
              <div className={css.heroActions}>
                <CTAButton
                  name="SearchPage"
                  to={defaultSearchLink}
                  className={css.primaryHeroButton}
                >
                  <FormattedMessage id="SectionHeroUuper.browseButton" />
                  <span className={css.heroButtonArrow} aria-hidden="true">
                    →
                  </span>
                </CTAButton>
                <CTAButton
                  name={signupRouteName}
                  variant="secondary"
                  className={css.secondaryHeroButton}
                >
                  <FormattedMessage id="SectionHeroUuper.browseButton2" />
                </CTAButton>
              </div>
            </div>
          </section>

          <div className={css.hostBanner}>
            <div className={css.hostBannerInner}>
              <span className={css.hostBannerQuestion}>
                <FormattedMessage id="LandingUuper.hostBannerQuestion" />
              </span>
              <CTAButton
                name="LandingUuper"
                variant="surface"
                size="compact"
                className={css.hostBannerButton}
              >
                <span className={css.hostBannerAction}>
                  <FormattedMessage id="LandingUuper.hostBannerAction" />
                </span>
              </CTAButton>
            </div>
          </div>

          <section className={css.listingsSection}>
            <div className={css.sectionHeading}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="LandingUuper.featuredListingsTitle" />
              </h2>
              <p className={css.sectionBodyCentered}>
                <FormattedMessage id="LandingUuper.featuredListingsSubtitle" />
              </p>
            </div>

            {displayedListings && displayedListings.length > 0 ? (
              <div className={css.listingsGrid}>
                {displayedListings.map(listing => (
                  <SearchListingCardV2
                    key={listing.id.uuid}
                    listing={listing}
                    renderSizes="(max-width: 767px) 100vw, (max-width: 1199px) 50vw, 33vw"
                  />
                ))}
              </div>
            ) : (
              <div className={css.emptyState}>
                <p className={css.emptyStateText}>
                  <FormattedMessage id="LandingUuper.noListings" />
                </p>
              </div>
            )}

            <div className={css.sectionActions}>
              <CTAButton
                name="SearchPage"
                to={defaultSearchLink}
                variant="primary"
                className={css.sectionButton}
              >
                <FormattedMessage id="LandingUuper.searchButton" />
              </CTAButton>
            </div>
          </section>

          <section className={css.introSection}>
            <div className={css.introGrid}>
              <div className={css.introContent}>
                <h2 className={css.sectionTitle}>
                  <FormattedMessage id="LandingUuper.introTitle" />
                </h2>
                <p className={css.sectionBody}>
                  <FormattedMessage id="LandingUuper.introParagraph1" />
                </p>
                <p className={css.sectionBody}>
                  <FormattedMessage id="LandingUuper.introParagraph2" />
                </p>
                <p className={css.sectionLead}>
                  <FormattedMessage id="LandingUuper.introHighlight" />
                </p>
              </div>

              <div className={css.introMedia}>
                <img
                  src={storefrontPreview}
                  alt={intl.formatMessage({ id: 'LandingUuper.introImageAlt' })}
                  className={css.introImage}
                />
              </div>
            </div>
          </section>

          <section className={css.storySection}>
            <div className={css.storyInner}>
              <div className={css.storyHeading}>
                <h2 className={css.storyTitle}>
                  <FormattedMessage id="SectionTwoUuperNew.card1.mainTitle" />
                </h2>
                <p className={css.storySubtitle}>
                  <FormattedMessage id="LandingUuper.explainerSubtitle" />
                </p>
                <div className={css.storyAudience}>
                  <button
                    type="button"
                    className={`${css.storyAudienceButton} ${
                      isUuperAudience ? css.storyAudiencePrimary : css.storyAudienceSecondary
                    }`}
                    onClick={() => setStoryAudience(STORY_AUDIENCE_UPER)}
                  >
                    <FormattedMessage id="SectionTwoUuperNew.card1.button1" />
                  </button>
                  <button
                    type="button"
                    className={`${css.storyAudienceButton} ${
                      isUuperAudience ? css.storyAudienceSecondary : css.storyAudiencePrimary
                    }`}
                    onClick={() => setStoryAudience(STORY_AUDIENCE_UUPO)}
                  >
                    <FormattedMessage id="SectionTwoUuperNew.card1.button2" />
                  </button>
                </div>
              </div>

              <div className={css.storyGrid}>
                {storyCards.map(card => (
                  <article
                    key={intl.formatMessage({ id: card.title.props.id })}
                    className={css.storyCard}
                  >
                    <div className={css.storyIconWrap}>
                      <img src={card.icon} alt="" className={css.storyIcon} />
                    </div>
                    <h3 className={css.storyCardTitle}>{card.title}</h3>
                    <p className={css.storyCardText}>{card.description}</p>
                  </article>
                ))}
              </div>
            </div>
          </section>
          <div className={css.storyAwning} aria-hidden="true">
            <svg viewBox="0 0 1440 40" preserveAspectRatio="none">
              <path
                d="M0,0 L0,0 Q60,40 120,0 Q180,40 240,0 Q300,40 360,0 Q420,40 480,0 Q540,40 600,0 Q660,40 720,0 Q780,40 840,0 Q900,40 960,0 Q1020,40 1080,0 Q1140,40 1200,0 Q1260,40 1320,0 Q1380,40 1440,0 L1440,0 Z"
                fill="#91D8AE"
              />
            </svg>
          </div>

          <section className={css.citiesSection}>
            <div className={css.sectionHeading}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="LandingUuper.citiesSectionTitle" />
              </h2>
              <p className={css.sectionBodyCentered}>
                <FormattedMessage id="LandingUuper.citiesSectionSubtitle" />
              </p>
            </div>

            <div className={css.cityGrid}>
              {featuredCityCardKeys.map(key => {
                const city = cityContent[key];
                if (!city) {
                  return null;
                }

                return (
                  <NamedLink key={key} name={city.routeName} className={css.cityCard}>
                    <div
                      className={css.cityCardImageWrap}
                      style={{ backgroundImage: `url(${city.image})` }}
                    />
                    <div className={css.cityCardFooter}>
                      <span className={css.cityCardTitle}>{resolveCityLabel(intl, key)}</span>
                    </div>
                  </NamedLink>
                );
              })}
            </div>

            <div className={css.sectionActions}>
              <NamedLink name="LandingPageCities" className={css.cityActionLink}>
                <FormattedMessage id="LandingUuper.viewAllCitiesButton" />
              </NamedLink>
              <CTAButton name="SearchPage" to={defaultSearchLink} className={css.cityActionButton}>
                <FormattedMessage id="SectionHeroUuper.browseButton" />
              </CTAButton>
            </div>
          </section>

          <section className={css.assuranceSection}>
            <div className={css.sectionHeading}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="SectionHowItWorks.titleLineOne" />
              </h2>
            </div>

            <div className={css.assuranceGrid}>
              <article className={css.assuranceCard}>
                <div className={css.assuranceIconWrap}>
                  <ShieldIcon />
                </div>
                <h3 className={css.assuranceTitle}>
                  <FormattedMessage id="SectionHowItWorks.part1Title" />
                </h3>
                <p className={css.assuranceText}>
                  <FormattedMessage id="SectionHowItWorks.part1Text" />
                </p>
              </article>

              <article className={css.assuranceCard}>
                <div className={css.assuranceIconWrap}>
                  <CardIcon />
                </div>
                <h3 className={css.assuranceTitle}>
                  <FormattedMessage id="SectionHowItWorks.part2Title" />
                </h3>
                <p className={css.assuranceText}>
                  <FormattedMessage id="SectionHowItWorks.part2Text" />
                </p>
              </article>

              <article className={css.assuranceCard}>
                <div className={css.assuranceIconWrap}>
                  <SupportIcon />
                </div>
                <h3 className={css.assuranceTitle}>
                  <FormattedMessage id="SectionHowItWorks.part3Title" />
                </h3>
                <p className={css.assuranceText}>
                  <FormattedMessage id="SectionHowItWorks.part3Text" />
                  <a href="mailto:hola@uupo.es" className={css.assuranceEmail}>
                    hola@uupo.es
                  </a>
                </p>
              </article>
            </div>
          </section>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

const { arrayOf, bool, object } = PropTypes;

LandingUuperComponent.propTypes = {
  history: object.isRequired,
  intl: intlShape.isRequired,
  featuredListings: arrayOf(object),
  featuredListingsInProgress: bool.isRequired,
  featuredListingsLoaded: bool.isRequired,
  cityListingCounts: object,
  cityListingCountsLoaded: bool.isRequired,
  fetchCityListingCountsInProgress: bool.isRequired,
  latestListings: arrayOf(object),
  isAuthenticated: bool.isRequired,
  onLoadFeaturedListings: PropTypes.func.isRequired,
  onFetchCityListingCounts: PropTypes.func.isRequired,
  scrollingDisabled: bool.isRequired,
};

LandingUuperComponent.defaultProps = {
  featuredListings: [],
  cityListingCounts: {},
  latestListings: [],
};

const mapStateToProps = state => {
  const { currentUserHasListings } = state.user;
  const { isAuthenticated } = state.Auth;
  const {
    latestListingsIds,
    featuredListingsIds,
    getFeaturedListingsInProgress,
    hasLoadedFeaturedListings,
  } = state.LandingPage;
  const latestListings = getListingsById(state, latestListingsIds);
  const featuredListings = getListingsById(state, featuredListingsIds);
  const {
    cityListingCounts,
    cityListingCountsLoaded,
    fetchCityListingCountsInProgress,
  } = state.LandingPageCities || {};

  return {
    featuredListings,
    featuredListingsInProgress: getFeaturedListingsInProgress,
    featuredListingsLoaded: hasLoadedFeaturedListings,
    cityListingCounts,
    cityListingCountsLoaded: !!cityListingCountsLoaded,
    fetchCityListingCountsInProgress: !!fetchCityListingCountsInProgress,
    latestListings,
    isAuthenticated,
    currentUserHasListings,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onLoadFeaturedListings: () => dispatch(getFeaturedListings()),
  onFetchCityListingCounts: () => dispatch(fetchCityListingCounts()),
});

const LandingUuper = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(LandingUuperComponent);

export default LandingUuper;
